import { InlineMessage, Typography } from '@volvo/vcdk-react';
import { Component } from 'react';

import type { ComponentType, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ComponentErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ComponentErrorBoundary caught an error:', error, errorInfo);
    }

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return import.meta.env.DEV ? (
        <InlineMessage
          variant="error"
          title="Something went wrong"
          icon="warning-brake-connected-safety"
          closable={false}
        >
          <Typography variant="body" className="">
            {this.state.error?.message || 'An unexpected error occurred in this component'}
          </Typography>
          {this.state.error?.stack && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm">Stack trace (dev only)</summary>
              <pre className="mt-2 overflow-auto rounded p-3 text-xs">{this.state.error.stack}</pre>
            </details>
          )}
        </InlineMessage>
      ) : null;
    }

    return this.props.children;
  }
}

// HOC to wrap any component with ComponentErrorBoundary
export function withComponentErrorBoundary<P extends object>(
  WrappedComponent: ComponentType<P>,
  fallback?: ReactNode,
  onError?: (error: Error, errorInfo: ErrorInfo) => void
) {
  const ComponentWithErrorBoundary = (props: P) => (
    <ComponentErrorBoundary fallback={fallback} onError={onError}>
      <WrappedComponent {...props} />
    </ComponentErrorBoundary>
  );

  // Preserve original component properties for better IDE support
  ComponentWithErrorBoundary.displayName = `withComponentErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  // Copy static properties from the wrapped component
  Object.assign(ComponentWithErrorBoundary, WrappedComponent);

  return ComponentWithErrorBoundary as ComponentType<P>;
}
